import React, { useState } from 'react';
import {
    Bell,
    Mail,
    Slack,
    Webhook,
    FileSpreadsheet,
    Zap,
    Puzzle,
    MessageCircle,
    Monitor,
    Smartphone
} from 'lucide-react';
import NotificationConfigView from './NotificationConfigView';

const INTEGRATIONS = [
    { id: 'discord', label: 'Discord', icon: <MessageCircle className="w-6 h-6" />, description: 'Receive notifications in your Discord server.', comingSoon: false },
    { id: 'whatsapp', label: 'WhatsApp', icon: <Smartphone className="w-6 h-6" />, description: 'Receive notifications via WhatsApp messages.', comingSoon: false },
    { id: 'push', label: 'Desktop Push Notification', icon: <Monitor className="w-6 h-6" />, description: 'Get native browser notifications on your desktop.', comingSoon: false },
    { id: 'email', label: 'Email Notifications', icon: <Mail className="w-6 h-6" />, description: 'Receive daily or weekly digest of new job matches directly to your inbox.', comingSoon: true },
    { id: 'slack', label: 'Slack Integration', icon: <Slack className="w-6 h-6" />, description: 'Get instant alerts in your team\'s Slack channel whenever a high-value job is posted.', comingSoon: true },
    { id: 'webhook', label: 'Webhook', icon: <Webhook className="w-6 h-6" />, description: 'Send job payloads to any custom URL.', comingSoon: true },
    { id: 'sheets', label: 'Google Sheets', icon: <FileSpreadsheet className="w-6 h-6" />, description: 'Automatically add new job matches to a Google Sheet row.', comingSoon: true },
    { id: 'zapier', label: 'Zapier', icon: <Zap className="w-6 h-6" />, description: 'Connect with 5000+ apps via Zapier.', comingSoon: true },
    { id: 'custom', label: 'Custom Integration', icon: <Puzzle className="w-6 h-6" />, description: 'Build your own custom integration using our API.', comingSoon: true },
];

export default function NotificationsView() {
    const [selectedIntegration, setSelectedIntegration] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('integration');
        return INTEGRATIONS.find(i => i.id === id) || null;
    });

    const handleSelectIntegration = (item) => {
        setSelectedIntegration(item);
        const url = new URL(window.location);
        url.searchParams.set('integration', item.id);
        window.history.pushState({}, '', url);
    };

    const handleBack = () => {
        setSelectedIntegration(null);
        const url = new URL(window.location);
        url.searchParams.delete('integration');
        window.history.pushState({}, '', url);
    };

    if (selectedIntegration) {
        return <NotificationConfigView integration={selectedIntegration} onBack={handleBack} />;
    }

    return (
        <div className="max-w-[1000px] mx-auto w-full pt-8 pb-12">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Setup notifications & integrations</h1>
                <p className="text-gray-500">Setup new notification or integration to start receiving alerts and automate your workflows.</p>
            </div>

            <div className="grid gap-4">
                {INTEGRATIONS.map((item) => (
                    <div key={item.id} className={`bg-white rounded-xl border border-gray-200 p-6 flex items-start justify-between shadow-sm transition-opacity ${item.comingSoon ? 'opacity-70 hover:opacity-100' : ''}`}>
                        <div className="flex gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${item.comingSoon ? 'bg-gray-50 text-gray-600' : 'bg-blue-50 text-blue-600'}`}>
                                {item.icon}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold text-gray-900">{item.label}</h3>
                                    {item.comingSoon && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium border border-gray-200">Coming Soon</span>}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            disabled={item.comingSoon}
                            onClick={() => handleSelectIntegration(item)}
                            className={`px-4 py-2 font-medium rounded-lg transition-colors ${item.comingSoon ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        >
                            {item.comingSoon ? 'Connect' : 'Configure'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
